// Mapa de la flota del proveedor (PP-51): donde estan sus vehiculos ahora, en
// todas sus plantas o en la filtrada en la barra. Es la "torre del proveedor"
// que pidio un proveedor real: toda la flota, o solo los autos con viaje en
// curso o asignado. Es su proyeccion, no la de la torre de la planta (ADR-014
// §6): la planta solo ve los moviles en viaje para ella.
//
// Lee una sola lectura multiplanta (GET /provider/positions, ADR-025 §9) cada
// 15 segundos, por polling como el resto del portal. Funciona igual con
// cualquier origen de posicion (simulador, app del conductor o telemetria):
// el mapa dibuja coordenadas e instantes, no sabe de donde vienen.
//
// Sin clave de Google en el build, o si Google no carga, la lista y la ficha
// siguen: el mapa es una zona mas, como en la torre.
//
// Rediseño (docs/PLAN-REDISENO-PORTAL.md §6.5): el mapa ocupa el ancho; la
// lista "En calle" flota a la izquierda y la ficha abajo a la derecha, ambas
// translucidas (el unico lugar donde el design system lo permite). En 390 px
// la ficha va debajo del mapa y la lista al pie.
import { useCallback, useMemo, useState } from "react";
import type { ProviderFleetPositionsResponse, ProviderFleetVehicle } from "@ucorp/contracts";
import { googleMapsLoader, type GoogleMapsLoader, type MapProvider } from "@ucorp/web-map";
import { Alert } from "../components/ui/alert.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { cn } from "../components/ui/cn.js";
import { EmptyState } from "../components/ui/empty.js";
import { Failure } from "../components/ui/failure.js";
import { Segmented } from "../components/ui/segmented.js";
import { SkeletonRows } from "../components/ui/skeleton.js";
import { readMapProvider } from "../config.js";
import { StaleTag } from "../home/StaleTag.js";
import { VEHICLE_TYPE_LABEL } from "../logic/fleet.js";
import {
  fleetBounds,
  minutesSince,
  siteTimezone,
  sortForList,
  vehicleKey,
  vehicleSiteId,
  vehiclesInScope,
  type FleetFilter,
} from "../logic/fleet-map.js";
import { formatClock } from "../logic/requests.js";
import { usePolled } from "../multi-site/usePolled.js";
import type { MultiSitePort } from "../multi-site/port.js";
import { systemClock, type Clock } from "../requests/clock.js";
import type { SectionContext } from "../shell/types.js";
import { GoogleFleetMap, type FleetMapVehicle } from "./GoogleFleetMap.js";

/** Cadencia de la encuesta: la del simulador y la app del conductor, que reportan cada 15 s a 1 min. */
export const POSITIONS_POLL_MS = 15_000;

interface Props {
  context: SectionContext;
  port: MultiSitePort;
  clock?: Clock;
  /** Por omision sale de las variables de build; los tests lo fijan. */
  mapProvider?: MapProvider;
  /** Por omision el de @ucorp/web-map; los tests inyectan uno falso. */
  loader?: GoogleMapsLoader;
}

const FILTERS: readonly { value: FleetFilter; label: string }[] = [
  { value: "with_trip", label: "Con viaje" },
  { value: "all", label: "Toda la flota" },
];

const TRIP_STATUS_LABEL = { in_progress: "En viaje", scheduled: "Asignado" } as const;

function ago(minutes: number): string {
  if (minutes === 0) return "hace menos de un minuto";
  return minutes === 1 ? "hace 1 minuto" : `hace ${String(minutes)} minutos`;
}

function statusOf(vehicle: ProviderFleetVehicle): string {
  return vehicle.trip === undefined ? "Sin viaje" : TRIP_STATUS_LABEL[vehicle.trip.status];
}

/** Punto de estado: en viaje verde, asignado acero, sin viaje gris (la misma clave que los pines). */
function dotOf(vehicle: ProviderFleetVehicle): string {
  if (vehicle.trip === undefined) return "bg-texto-debil";
  return vehicle.trip.status === "in_progress" ? "bg-ok" : "bg-ambar";
}

function siteNameOf(data: ProviderFleetPositionsResponse, vehicle: ProviderFleetVehicle): string {
  const siteId = vehicleSiteId(vehicle);
  return vehicle.trip?.siteName ?? data.sites.find((s) => s.siteId === siteId)?.siteName ?? "";
}

function toMapVehicle(vehicle: ProviderFleetVehicle): FleetMapVehicle {
  return {
    key: vehicleKey(vehicle),
    code: vehicle.code,
    type: vehicle.type,
    coordinates: vehicle.position.coordinates,
    instant: vehicle.position.instant,
    title: `${vehicle.code} · ${statusOf(vehicle)}`,
    onTrip: vehicle.trip !== undefined,
  };
}

function Detail({ data, vehicle, now, onClose }: { data: ProviderFleetPositionsResponse; vehicle: ProviderFleetVehicle; now: number; onClose: () => void }): React.JSX.Element {
  const timezone = siteTimezone(data, vehicle.position.siteId);
  const clock = timezone === undefined ? "" : formatClock(vehicle.position.instant, timezone);
  return (
    <section
      aria-label={`Ficha del móvil ${vehicle.code}`}
      data-testid="ficha-vehiculo"
      className="panel-flotante entrada-sube flex flex-col gap-2.5 p-3.5 md:absolute md:bottom-3 md:right-3 md:z-10 md:w-[340px]"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="m-0 text-base font-semibold text-texto">Móvil {vehicle.code}</h2>
          <p className="tabular m-0 text-xs text-texto-suave">
            {vehicle.plate} · {VEHICLE_TYPE_LABEL[vehicle.type]}
          </p>
        </div>
        <Button variant="fantasma" size="sm" onClick={onClose} aria-label="Cerrar la ficha">
          Cerrar
        </Button>
      </div>
      {vehicle.trip === undefined ? (
        <p className="m-0 text-sm text-texto-2">Sin viaje en curso ni asignado para hoy.</p>
      ) : (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="text-texto-suave">Viaje</dt>
          <dd className="tabular m-0 text-texto">{TRIP_STATUS_LABEL[vehicle.trip.status]} · {vehicle.trip.tripId}</dd>
          <dt className="text-texto-suave">Planta</dt>
          <dd className="m-0 text-texto">{vehicle.trip.siteName}</dd>
          <dt className="text-texto-suave">Servicio</dt>
          <dd className="m-0 text-texto">{VEHICLE_TYPE_LABEL[vehicle.trip.vehicleType]}</dd>
          <dt className="text-texto-suave">Conductor</dt>
          <dd className="m-0 text-texto">{vehicle.trip.driverName ?? "Sin conductor asignado"}</dd>
        </dl>
      )}
      <p className="m-0 text-xs text-texto-suave">
        Última posición {ago(minutesSince(vehicle.position.instant, now))}
        {clock === "" ? "" : ` (${clock})`}
        {vehicle.position.source === "simulated" && (
          <Badge className="ml-2" variant="neutro">
            Simulada
          </Badge>
        )}
      </p>
    </section>
  );
}

export function FleetMap({ context, port, clock = systemClock, mapProvider, loader = googleMapsLoader }: Props): React.JSX.Element {
  const { session } = context;
  const read = useCallback(() => port.fleetPositions(session), [port, session]);
  const { state } = usePolled(read, POSITIONS_POLL_MS, clock);
  const [filter, setFilter] = useState<FleetFilter>("all");
  const [selected, setSelected] = useState<string | null>(null);
  const provider = useMemo(() => mapProvider ?? readMapProvider(), [mapProvider]);

  const siteIds = useMemo(
    () => new Set(context.siteId === null ? context.sites.map((s) => s.siteId) : [context.siteId]),
    [context.siteId, context.sites],
  );
  const data = state.estado === "listo" ? state.data : null;
  const inSites = useMemo(() => (data === null ? [] : vehiclesInScope(data.vehicles, "all", siteIds)), [data, siteIds]);
  const shown = useMemo(() => sortForList(vehiclesInScope(inSites, filter, siteIds)), [inSites, filter, siteIds]);
  const mapVehicles = useMemo(() => shown.map(toMapVehicle), [shown]);
  // Sin vehiculos con el filtro, el encuadre sigue mostrando a la flota de la planta.
  const bounds = useMemo(() => fleetBounds(shown.length > 0 ? shown : inSites), [shown, inSites]);
  const fitKey = `${filter}|${context.siteId ?? "*"}|${String(bounds !== null)}`;
  const selectedVehicle = shown.find((v) => vehicleKey(v) === selected) ?? null;
  const now = clock.now();
  const onTrip = inSites.filter((v) => v.trip !== undefined).length;

  return (
    <section aria-labelledby="mapa-flota-titulo" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 id="mapa-flota-titulo" className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-blanco">
            Mapa de la flota
          </h1>
          {data !== null && inSites.length > 0 ? (
            <p className="tabular m-0 text-xs text-texto-suave" aria-live="polite">
              {onTrip} con viaje · {inSites.length} con posición en los últimos {String(data.freshnessMinutes)} minutos
              {state.estado === "listo" && state.stale && (
                <>
                  {" "}
                  <StaleTag />
                </>
              )}
            </p>
          ) : (
            <p className="m-0 text-xs text-texto-suave">Posiciones de la flota de la empresa, de todas las plantas o de la filtrada. Se actualiza sola.</p>
          )}
        </div>
        <Segmented<FleetFilter> label="Qué vehículos mostrar" value={filter} options={FILTERS} onChange={setFilter} />
      </div>

      {state.estado === "cargando" && <SkeletonRows rows={3} label="Cargando posiciones" />}
      {state.estado === "denegado" && <Failure title="Esta cuenta no puede ver la flota." />}
      {state.estado === "error" && <Failure title="No se pudieron leer las posiciones. Se vuelve a intentar sola en unos segundos." />}

      {data !== null && inSites.length === 0 && (
        <EmptyState icon="mapa" title="Todavía no hay posiciones de tu flota">
          El mapa muestra los vehículos con una posición de los últimos {String(data.freshnessMinutes)} minutos. Las posiciones llegan mientras los vehículos hacen viajes.
        </EmptyState>
      )}

      {data !== null && inSites.length > 0 && (
        <>
          {state.estado === "listo" && state.stale && (
            <Alert variant="aviso">No se pudo actualizar: se muestra la última lectura y se vuelve a intentar sola.</Alert>
          )}
          <div className="relative flex flex-col gap-3">
            <div className="relative h-[calc(100vh-240px)] min-h-[420px] overflow-hidden rounded-lg border border-linea bg-superficie">
              {provider.kind === "google" ? (
                <GoogleFleetMap
                  apiKey={provider.apiKey}
                  mapId={provider.mapId}
                  loader={loader}
                  vehicles={mapVehicles}
                  selected={selectedVehicle === null ? null : vehicleKey(selectedVehicle)}
                  onSelect={setSelected}
                  bounds={bounds}
                  fitKey={fitKey}
                  glideMs={POSITIONS_POLL_MS}
                />
              ) : (
                <div role="status" className="grilla-tecnica flex h-full flex-col items-center justify-center gap-1 p-4 text-center">
                  <p className="m-0 text-sm font-semibold text-texto">Mapa no configurado</p>
                  <p className="m-0 text-sm text-texto-suave">Este build del portal no tiene la clave de Google Maps. La lista de la flota sigue funcionando.</p>
                </div>
              )}
              {shown.length === 0 && (
                <p className="panel-flotante absolute inset-x-3 top-3 m-0 p-3 text-sm text-texto md:left-[324px]" role="status">
                  Ningún vehículo con viaje tiene posición en los últimos {String(data.freshnessMinutes)} minutos.
                </p>
              )}
            </div>

            {selectedVehicle !== null && <Detail data={data} vehicle={selectedVehicle} now={now} onClose={() => setSelected(null)} />}

            <aside
              aria-label="En calle"
              className="panel-flotante flex flex-col md:absolute md:left-3 md:top-3 md:z-10 md:max-h-[calc(100%-24px)] md:w-[300px]"
            >
              <div className="flex min-h-10 items-center gap-2 border-b border-divisor px-3.5 py-2">
                <h2 className="rotulo m-0 text-texto-suave">En calle</h2>
                <b className="text-eyebrow font-semibold text-texto">{shown.length}</b>
              </div>
              <ul className="m-0 flex list-none flex-col overflow-y-auto p-0" aria-label="Vehículos en el mapa">
                {shown.map((v) => {
                  const key = vehicleKey(v);
                  const isSelected = key === selected;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => setSelected(isSelected ? null : key)}
                        className={cn(
                          "grid min-h-fila w-full grid-cols-[52px_minmax(0,1fr)_auto] items-center gap-x-2.5 border-t border-divisor px-3.5 py-1 text-left text-xs transition-colors duration-150 ease-estandar first:border-t-0",
                          isSelected ? "bg-velo-activo" : "hover:bg-velo",
                        )}
                      >
                        <span className="tabular text-sm font-semibold text-texto">{v.code}</span>
                        <span className="flex min-w-0 flex-col leading-tight">
                          {/* En la fila elegida el velo suma fondo: los apagados suben un tono para seguir en AA. */}
                          <span className={cn("truncate", isSelected ? "text-texto-2" : "text-texto-suave")}>{siteNameOf(data, v)}</span>
                          <span className={cn("inline-flex items-center gap-1.5", v.trip === undefined ? (isSelected ? "text-texto-2" : "text-texto-suave") : "font-medium text-texto-2")}>
                            <i aria-hidden="true" className={cn("inline-block size-1.5 rounded-full", dotOf(v))} />
                            {statusOf(v)}
                          </span>
                        </span>
                        <span className={cn("tabular whitespace-nowrap", isSelected ? "text-texto-2" : "text-texto-debil")}>{ago(minutesSince(v.position.instant, now))}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>
          </div>
        </>
      )}
    </section>
  );
}
